import AsyncStorage from '@react-native-async-storage/async-storage'
import { RESET_STATE } from '@redux-offline/redux-offline/lib/constants'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import merge from 'lodash/merge'
import { Platform } from 'react-native'
import { Action } from 'redux'
import { combineEpics } from 'redux-observable'
import { Observable, of } from 'rxjs'
import { filter, map, switchMap } from 'rxjs/operators'
import { SETTINGS_ACCEPTED_TERMS_AND_CONDITIONS_VERSION } from '~constants/Settings'
import {
  auth,
  analytics,
  signOut,
  crashlytics,
  remoteConfig,
  getNumber,
  setUserId,
  setUserProperties,
  getIdTokenResult,
  setCrashlyticsUserId,
  getString,
} from '~providers/firebase'
import { Profile, UserClaims, UserWithClaims } from '~types'
import defaultConfig from '../../config'
import { User } from 'firebase/auth'
import { RollbarErrorTracking } from '~providers/rollbar'

export interface AuthState {
  readonly loading: 'idle' | 'pending' | 'succeeded' | 'failed'
  readonly isAuthenticated: boolean
  readonly user: UserWithClaims | null
  readonly profile: Profile
  readonly termsAndConditions: string
}

const initialProfile: Profile = {
  displayName: '',
  count_classes: 0,
  permissions: {
    restrictions_max_classes: defaultConfig.restrictions_max_classes,
    restrictions_max_students_per_classes: defaultConfig.restrictions_max_students_per_classes,
    restrictions_max_chats_per_class: defaultConfig.restrictions_max_chats_per_class,
  },
}

const initialState: AuthState = {
  loading: 'idle',
  isAuthenticated: false,
  user: null,
  profile: initialProfile,
  termsAndConditions: defaultConfig.terms_and_conditions_version,
}

export const acceptTermsAndConditions = createAsyncThunk('auth/terms_and_conditions', async () => {
  const version = getString(remoteConfig, 'terms_and_conditions_version')

  await AsyncStorage.setItem(SETTINGS_ACCEPTED_TERMS_AND_CONDITIONS_VERSION, version)

  return version
})

export const logout = createAsyncThunk('auth/logout', async () => {
  await signOut(auth)

  // Microsoft MSAL stores in sessionStorage
  if (Platform.OS === 'web') {
    try {
      sessionStorage.removeItem('msal.idtoken')
    } catch (error) {
      console.log(error)
    }
  }
})

export const getAuth = createAsyncThunk(
  'auth/get_auth',
  async (user: User | null): Promise<UserWithClaims> => {
    if (user == null) {
      throw new Error('Not logged in')
    }

    const result = await getIdTokenResult(user)

    return {
      uid: user.uid,
      email: user.email,
      emailVerified: user.emailVerified,
      displayName: user.displayName,
      tenantId: user.tenantId,
      claims: result.claims as UserClaims,
    }
  },
)

const reducer = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authStateChanged(state, action: PayloadAction<UserWithClaims | undefined>) {
      // TODO: Shouldn't happen in reducer
      setCrashlyticsUserId(crashlytics, action.payload?.claims.sub ?? '')
      RollbarErrorTracking.configure({
        payload: {
          person: {
            id: action.payload?.claims?.sub ?? null,
            ...(action.payload?.claims?.customer_id && {
              customer_id: action.payload?.claims?.customer_id,
            }),
          },
        },
      })
      setUserId(analytics, action.payload?.claims.sub ?? null)
      setUserProperties(analytics, {
        customer_id: action.payload?.claims.customer_id ?? null,
      })
      state.isAuthenticated = action.payload !== undefined
      state.user = action.payload ?? null
      state.loading = 'succeeded'
    },
    setProfile(state, action: PayloadAction<Profile | undefined>) {
      state.profile = merge(
        {},
        initialProfile,
        {
          permissions: {
            restrictions_max_classes: getNumber(remoteConfig, 'restrictions_max_classes'),
            restrictions_max_students_per_classes: getNumber(
              remoteConfig,
              'restrictions_max_students_per_classes',
            ),
            restrictions_max_chats_per_class: getNumber(
              remoteConfig,
              'restrictions_max_chats_per_class',
            ),
          },
        },
        action.payload,
      )
    },
    setTermsAndConditions(state, action: PayloadAction<string>) {
      state.termsAndConditions = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(acceptTermsAndConditions.fulfilled, (state, action) => {
        state.termsAndConditions = action.payload
      })
      .addCase(logout.fulfilled, (state, action) => {
        state.isAuthenticated = false
        state.user = null
        state.profile = initialProfile
        state.loading = 'succeeded'
      })
  },
})

const getAuthFulfilledEpic = (action$: Observable<Action>) =>
  action$.pipe(
    filter(getAuth.fulfilled.match),
    switchMap((action) => {
      if (action.payload.claims.aud !== auth.app.options.projectId) {
        // Logout and clear outbox
        return of(logout(), { type: RESET_STATE })
      } else {
        return of(authStateChanged(action.payload))
      }
    }),
  )

const getAuthRejectedEpic = (action$: Observable<Action>) =>
  action$.pipe(
    filter(getAuth.rejected.match),
    map(() => authStateChanged(undefined)),
  )

const epic = combineEpics(getAuthFulfilledEpic, getAuthRejectedEpic)
export const { authStateChanged, setProfile, setTermsAndConditions } = reducer.actions
export const authReducer = reducer.reducer

export { epic as authEpic }
